import * as React from 'react'
import { ReactElement, useRef } from 'react'
import { useQueueJob } from '../../hooks/useQueue'
import { AnyQueueData } from '../../hooks/queue/queueTypes'
import { queueToastContent } from './queueToastRegistry'

/**
 * The body of a queue toast, rendered inside the toaster.
 *
 * This subscribes to its own job, so the toast keeps itself up to date as the job progresses and the dispatcher never
 * has to push new content into an existing toast.
 */
export default function QueueToastContent({ id, initialJob }: { id: string; initialJob?: AnyQueueData }): ReactElement {
    const job = useQueueJob<AnyQueueData>(id)

    // A finished job leaves the queue while its toast is still closing, so hold on to the last state it was in.
    //
    // The fallback covers the job that is already gone by the time this mounts: a job without a context is finished
    // the moment it is pushed, so the queue has let go of it before the toaster ever renders its content. Without the
    // snapshot the dispatcher handed us, such a toast would come up empty.
    const lastKnownJob = useRef(job || initialJob)
    if (job) {
        lastKnownJob.current = job
    }

    const current = lastKnownJob.current

    if (!current?.status?.current) {
        return null
    }

    const Content = queueToastContent[current.kind]

    return <Content id={id} job={current} />
}
